export type WorkflowCondition =
  | { field: 'loanRequired'; equals: 'yes' | 'no' }
  | { field: 'always'; equals: 'true' };

export type WorkflowStepFieldType = 'text' | 'number' | 'date' | 'select' | 'textarea' | 'boolean';

export type WorkflowStepFieldDef = {
  key: string;
  label: string;
  type: WorkflowStepFieldType;
  required?: boolean;
  options?: string[];
};

export type WorkflowStepDocDef = {
  key: string;
  label: string;
  required?: boolean;
};

export type WorkflowStepDef = {
  key: string;
  title: string;
  order: number;
  dependsOn?: string[];
  condition?: WorkflowCondition | null;
  kind?: 'task' | 'document' | 'payment' | 'form';
  fields?: WorkflowStepFieldDef[];
  requiredDocs?: WorkflowStepDocDef[];
};

export type WorkflowTemplateType = 'main' | 'loan';

export type WorkflowTemplateDoc = {
  name: string;
  type: WorkflowTemplateType;
  version: number;
  steps: WorkflowStepDef[];
  updatedAt?: any;
  updatedBy?: string | null;
  createdAt?: any;
  createdBy?: string | null;
};

export type WorkflowCaseType = 'main' | 'loan';

export type WorkflowCaseDoc = {
  caseType: WorkflowCaseType;
  status: 'active' | 'closed' | 'cancelled';
  bookingId: string;
  unitId: string;
  templateId: string;
  templateVersion: number;
  parentCaseId?: string | null;
  loanRequired?: 'yes' | 'no' | null;
  completedStepKeys: string[];
  stepData?: Record<string, any>;
  startedAt?: any;
  startedBy?: string | null;
  updatedAt?: any;
  updatedBy?: string | null;
  closedAt?: any;
  closedBy?: string | null;
  closeReason?: string | null;
  attemptNo?: number;
  preferredBankName?: string | null;
  switchedFromCaseId?: string | null;
  switchedFromBankName?: string | null;
  switchedToBankName?: string | null;
  switchReason?: string | null;
  archivedAt?: any;
  archivedBy?: string | null;
  archivedFromCaseId?: string | null;
};

export type StepComputedStatus = 'done' | 'ready' | 'blocked' | 'skipped';

export type WorkflowStepComputed = WorkflowStepDef & {
  status: StepComputedStatus;
  blockedBy?: string[];
};

export const DEFAULT_OFFICE_MAIN_TEMPLATE_ID = 'office_main_v1';
export const DEFAULT_OFFICE_LOAN_TEMPLATE_ID = 'office_loan_v1';

export const OFFICE_MAIN_STEP_KEYS = {
  customerKycBookingForm: 'customer_kyc_bookingform',
  downpaymentGst: 'downpayment_gst',
  autoGenerateAllotmentLetter: 'auto_generate_allotment_letter',
  sendToAdvocate: 'send_to_advocate',
  loanGateSanction: 'loan_gate_sanction',
  signAtAdvocate: 'sign_at_advocate',
  uploadSignedDocs: 'upload_signed_docs',
} as const;

export const OFFICE_LOAN_STEP_KEYS = {
  collectDocs: 'loan_collect_docs',
  sendToBanks: 'loan_send_to_banks',
  finaliseBank: 'loan_finalise_bank',
  followupSanction: 'loan_followup_sanction',
  sanctionReceived: 'loan_sanction_received',
  collectSendDocsLegacy: 'loan_collect_send_docs',
} as const;

export const defaultOfficeMainTemplate = (): WorkflowTemplateDoc => ({
  name: 'Office Booking Workflow (Standard)',
  type: 'main',
  version: 1,
  steps: [
    {
      key: OFFICE_MAIN_STEP_KEYS.customerKycBookingForm,
      title: 'Customer details + KYC + signed booking form',
      order: 1,
      dependsOn: [],
      kind: 'document',
      fields: [
        { key: 'expectedApplicantsCount', label: 'Applicants count', type: 'number', required: true },
        { key: 'finalized', label: 'Finalized', type: 'boolean', required: true },
      ],
      requiredDocs: [{ key: 'booking_form_signed', label: 'Signed booking form', required: true }],
    },
    {
      key: OFFICE_MAIN_STEP_KEYS.downpaymentGst,
      title: 'Down payment + GST payment + cheque/receipt upload',
      order: 2,
      dependsOn: [OFFICE_MAIN_STEP_KEYS.customerKycBookingForm],
      kind: 'payment',
      fields: [],
      requiredDocs: [],
    },
    {
      key: OFFICE_MAIN_STEP_KEYS.autoGenerateAllotmentLetter,
      title: 'Auto generate allotment letter draft',
      order: 3,
      dependsOn: [OFFICE_MAIN_STEP_KEYS.downpaymentGst],
      kind: 'task',
      fields: [],
      requiredDocs: [],
    },
  ],
});

export const defaultOfficeLoanTemplate = (): WorkflowTemplateDoc => ({
  name: 'Loan Workflow (Upto Sanction)',
  type: 'loan',
  version: 1,
  steps: [
    {
      key: OFFICE_LOAN_STEP_KEYS.collectDocs,
      title: 'Collect Documents for Loan',
      order: 1,
      dependsOn: [],
      kind: 'task',
      fields: [],
      requiredDocs: [],
    },
    {
      key: OFFICE_LOAN_STEP_KEYS.sendToBanks,
      title: 'Send Lead to SBI',
      order: 2,
      dependsOn: [],
      kind: 'task',
      fields: [],
      requiredDocs: [],
    },
    {
      key: OFFICE_LOAN_STEP_KEYS.finaliseBank,
      title: 'Finalise bank',
      order: 3,
      dependsOn: [OFFICE_LOAN_STEP_KEYS.collectDocs, OFFICE_LOAN_STEP_KEYS.sendToBanks],
      kind: 'form',
      fields: [],
      requiredDocs: [],
    },
    {
      key: OFFICE_LOAN_STEP_KEYS.followupSanction,
      title: 'Follow up for sanction letter',
      order: 4,
      dependsOn: [OFFICE_LOAN_STEP_KEYS.finaliseBank],
      kind: 'task',
      fields: [],
      requiredDocs: [],
    },
    {
      key: OFFICE_LOAN_STEP_KEYS.sanctionReceived,
      title: 'Sanction received + upload sanction letter',
      order: 5,
      dependsOn: [OFFICE_LOAN_STEP_KEYS.followupSanction],
      kind: 'document',
      fields: [],
      requiredDocs: [{ key: 'sanctionLetter', label: 'Sanction letter', required: true }],
    },
  ],
});

export function evaluateCondition(condition: WorkflowCondition | null | undefined, ctx: { loanRequired?: any }): boolean {
  if (!condition) return true;
  if (condition.field === 'always') return condition.equals === 'true';
  if (condition.field === 'loanRequired') {
    const v = String(ctx.loanRequired || '').toLowerCase();
    return v === condition.equals;
  }
  return true;
}

export function computeWorkflowSteps(args: {
  template: WorkflowTemplateDoc;
  completedStepKeys: string[];
  ctx: { loanRequired?: 'yes' | 'no' | null };
  derivedDoneKeys?: string[];
}): WorkflowStepComputed[] {
  const completed = new Set([...(args.completedStepKeys || []), ...((args.derivedDoneKeys || []).filter(Boolean) as string[])]);
  const ctx = args.ctx || {};
  const sorted = [...(args.template?.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  return sorted.map((s) => {
    const ok = evaluateCondition(s.condition, ctx);
    if (!ok) return { ...s, status: 'skipped' as const };
    if (completed.has(s.key)) return { ...s, status: 'done' as const };
    const deps = (s.dependsOn || []).filter(Boolean);
    const blockedBy = deps.filter((d) => !completed.has(d));
    if (blockedBy.length === 0) return { ...s, status: 'ready' as const };
    return { ...s, status: 'blocked' as const, blockedBy };
  });
}
